import {
  type GeneralizedPath,
  type InferredPage,
  type InferredPageId,
  type PathParameters,
} from '@/src/lib/pages';
import { type EdgeSafePageId, type PageAuthType } from '@/src/middleware/pages';

import { type Pages } from './config';

/**
 * A compile-type safeguard that ensures that the pages that exist in {@link Pages} are the same
 * as those that exist in {@link EdgeSafePages} by ID.
 */
type CompileSafePageId<T extends string> = [T] extends [EdgeSafePageId]
  ? [EdgeSafePageId] extends [T]
    ? T
    : never
  : never;

export type PageId = CompileSafePageId<InferredPageId<typeof Pages>>;

/**
 * Resolves the page object(s) in {@link Pages} whose `id` matches the generic parameter {@link I},
 * defaulting to the full union of configured pages.
 */
export type Page<I extends PageId = PageId> = Extract<InferredPage<typeof Pages>, { id: I }>;

export type PublicPageId = Extract<Page, { auth: PageAuthType.Public }>['id'];

export type Auth0AuthenticatedPageId = Extract<Page, { auth: PageAuthType.Auth0 }>['id'];

export type LtiPageId = Extract<Page, { auth: PageAuthType.Lti }>['id'];

export type PageIdForAuthType<T extends PageAuthType> = Extract<Page, { auth: T }>['id'];

export type PagePathParams<I extends PageId = PageId> = PathParameters<Page<I>['path']>;

type Equals<A, B> = A extends B ? (B extends A ? true : false) : false;

/**
 * Represents a subset of {@link PageId} for pages that are not associated with path parameters
 * in their path string (i.e. pages for which `PagePathParams<I>` does resolves to an empty object
 * type).
 *
 * @example
 * ```ts
 * // Not valid, because the path for this page is `/learner-cohorts/:id` and thus has a path
 * // parameter.
 * const pageIdWithPathParams: PageIdWithoutPathParams = 'learner-cohort';  // Type error
 *
 * // Valid, because the path for this page is `/dashboard` and thus does not have any path
 * // parameters.
 * const pageIdWithoutPathParams: PageIdWithoutPathParams = 'dashboard';
 * ```
 */
export type PageIdWithoutPathParams = keyof {
  [key in PageId as Equals<PagePathParams<key>, Record<string, string>> extends true
    ? key
    : never]: key;
};

/**
 * Represents a subset of {@link PageId} for pages that are associated with path parameters
 * in their path string (i.e. pages for which `PagePathParams<I>` does not resolve to an empty
 * object type).
 *
 * @example
 * ```ts
 * // Valid, because the path for this page is `/learner-cohorts/:id` and thus has a path parameter.
 * const pageIdWithPathParams: PageIdWithParams = 'learner-cohort';
 *
 * // Not valid, because the path for this page is `/dashboard` and thus does not have any path
 * // parameters.
 * const pageIdWithoutPathParams: PageIdWithParams = 'dashboard'; // Type error
 * ```
 */
export type PageIdWithParams<I extends PageId = PageId> = Exclude<I, PageIdWithoutPathParams>;

/**
 * A generic type that represents a definition of a {@link Page} and the associated path parameters
 * (if applicable) it requires to construct its path.
 *
 * {@link I} is a {@link PageIdWithParams}, then the type resolves to an object with an `id`
 * property that is the page ID as a string literal type and a `params` property that is an object
 * with keys corresponding to the path parameters required by the page's path and values of type
 * `string`.  If the generic type parameter {@link I} is a {@link PageIdWithoutPathParams}, then
 * the type type resolves to just the page ID as a string literal type.
 *
 * @example
 * ```ts
 * // For a page with path parameters:
 * type LearnerCohortPageDefinition = PageIdAndParams<'learner-cohort'>;
 * // Resolves to:
 * // { id: 'learner-cohort'; params: { id: string }; }
 *
 * // For a page without path parameters:
 * type DashboardPageDefinition = PageIdAndParams<'dashboard'>;
 * // Resolves to:
 * // 'dashboard'
 * ```
 */
export type PageIdAndParams<I extends PageId = PageId> = I extends PageIdWithoutPathParams
  ? I
  : I extends PageIdWithParams
    ? { readonly id: I; readonly params: PagePathParams<I> }
    : never;

/**
 * A generic type that represents the path associated with the ID defined by the generic type
 * parameter {@link I}, with {@link string} types replacing any path parameter placeholders.
 *
 * @example
 * GeneralizedPagePath<"rubric"> // `/rubrics/${string}`
 */
export type GeneralizedPagePath<I extends PageId = PageId> = I extends PageId
  ? GeneralizedPath<Page<I>['path']>
  : never;

export const PendingApprovalsPages = [
  'pending-approvals-activities',
  'pending-approvals-time-entries',
  'pending-approvals-observations',
] as const satisfies readonly PageId[];

export type PendingApprovalsPage = (typeof PendingApprovalsPages)[number];

export const isPendingApprovalsPage = (page: unknown): page is PendingApprovalsPage =>
  PendingApprovalsPages.includes(page as PendingApprovalsPage);

/**
 * The {@link PageId}s of the detail pages for the organization program content tree (programs,
 * levels, plans and activities), all of which are rendered under the `/organizationPrograms/*`
 * sub-routes.
 */
export const ProgramContentPages = [
  'activity',
  'level',
  'plan',
  'program',
] as const satisfies readonly PageId[];

export type ProgramContentPage = (typeof ProgramContentPages)[number];

export const isProgramContentPage = (page: unknown): page is ProgramContentPage =>
  ProgramContentPages.includes(page as ProgramContentPage);

type DistributedOmit<T, K extends keyof T> = T extends never ? never : Omit<T, K>;

/**
 * Returns a serializable version of the provided {@link Page} that is safe to pass between the
 * server and client (e.g. getServerSideProps or the {@link authenticatedPageContext}) by omitting
 * non-serializable properties such as `matcher` and `activeMatcher` (which have {@link RegExp})
 * values.
 *
 * @param {P} page
 *   The page to be transformed into a serializable version. This is typically a {@link Page} object
 *   that is derived from the `page` property of a {@link PageAuth} object in the context of an
 *   authenticated page.
 *
 * @returns {DistributedOmit<P, 'activeMatcher' | 'matcher'>}
 *   A serializable version of the provided page with the `matcher` and `activeMatcher` properties
 *   omitted. The returned type is a distributed omit of the input page type `P`, which means that
 *   if `P` is a union type, the omit will be applied to each member of the union individually.
 */
export const serializablePage = <P extends Page>(
  page: P,
): DistributedOmit<P, 'activeMatcher' | 'matcher'> => {
  const { activeMatcher: _, matcher: __, ...serializable } = page;
  return serializable as DistributedOmit<P, 'activeMatcher' | 'matcher'>;
};
